import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const StarField = () => {
    const { isDark } = useTheme();
    const [stars, setStars] = useState([]);
    const starRefs = useRef([]);
    const rafIdRef = useRef(null);
    const latestScrollYRef = useRef(0);
    const viewportHeightRef = useRef(typeof window !== 'undefined' ? window.innerHeight : 800);

    // Utility to pick a near-physical star color distribution (blue-white are rare, red giants are rare)
    const pickStarColor = useMemo(() => {
        return () => {
            const roll = Math.random();
            // Return [r, g, b] and a qualitative brightness multiplier for glow
            if (roll < 0.06) {
                // Blue-white (hot, rare)
                return { rgb: [166, 200, 255], glowMult: 1.15 };
            } else if (roll < 0.30) {
                // Cool white
                return { rgb: [220, 232, 255], glowMult: 1.0 };
            } else if (roll < 0.78) {
                // Neutral white (most common)
                return { rgb: [255, 255, 255], glowMult: 0.95 };
            } else if (roll < 0.96) {
                // Yellow-white
                return { rgb: [255, 244, 207], glowMult: 1.0 };
            }
            // Warm orange-red (very rare)
            return { rgb: [255, 214, 166], glowMult: 1.1 };
        };
    }, []);

    useEffect(() => {
        // Generate random stars
        const generateStars = () => {
            const starCount = 110; // keep modest density for performance
            const newStars = [];

            for (let i = 0; i < starCount; i++) {
                // Weighted depth distribution for stronger parallax contrast
                // ~22% very far (almost fixed), ~58% mid, ~20% near (moves a lot)
                const bucket = Math.random();
                let depth;
                let size;
                if (bucket < 0.22) {
                    depth = Math.random() * 0.015 + 0.005; // 0.005 - 0.02 (farther, moves less)
                    size = Math.random() * 0.6 + 0.7; // smaller far stars
                } else if (bucket < 0.80) {
                    depth = Math.random() * 0.12 + 0.06; // 0.06 - 0.18 (mid)
                    size = Math.random() * 1.2 + 0.9; // medium
                } else {
                    depth = Math.random() * 0.38 + 0.24; // 0.24 - 0.62 (nearer but toned-down)
                    size = Math.random() * 1.6 + 1.3; // larger near stars
                }

                const { rgb, glowMult } = pickStarColor();
                const [r, g, b] = rgb;
                // Randomize base opacity and twinkle range by distance and theme
                const baseOpacity = (isDark ? 0.25 : 0.10) + Math.random() * (isDark ? 0.45 : 0.20);
                const twinkleAmp = Math.max(0.05, Math.min(0.5, 0.15 + depth * 0.6 + Math.random() * 0.1));
                const opacityMin = Math.max(0.05, baseOpacity - twinkleAmp * 0.5);
                const opacityMax = Math.min(1.0, baseOpacity + twinkleAmp);
                const opacityMid = (opacityMin + opacityMax) / 2;
                const animationDuration = 2.4 + Math.random() * 3.2; // 2.4s - 5.6s
                const animationDelay = Math.random() * 5.0; // de-phase
                // Glow radius scales with size and brightness
                const glowBase = (size * 2 + depth * 2) * glowMult;
                // Clamp to reduce expensive large blurs
                const glowSoft = Math.min(10, Math.max(2, glowBase * 1.5));
                const useSolid = size < 1.0 && depth < 0.03; // very far faint stars: solid dot, no gradient

                newStars.push({
                    id: i,
                    leftPercent: Math.random() * 100,
                    baseTopPx: Math.random() * viewportHeightRef.current,
                    size,
                    opacityMin,
                    opacityMax,
                    animationDelay,
                    animationDuration,
                    // Parallax depth factor: nearer stars move more, farther stars move less
                    depth,
                    color: { r, g, b },
                    glowPx: glowSoft,
                    opacityMid,
                    useSolid,
                });
            }

            setStars(newStars);
        };

        generateStars();
    }, [isDark, pickStarColor]);

    useEffect(() => {
        // Parallax updater driven by rAF; reacts to scroll position changes
        const getScrollY = () => {
            return (
                window.pageYOffset ||
                document.documentElement.scrollTop ||
                document.body.scrollTop ||
                0
            );
        };

        const applyParallax = (scrollY) => {
            const xBase = 0.02; // reduced horizontal drift
            const yBase = 0.10; // reduced vertical motion base
            const yDepthMult = 0.20; // reduced depth amplification
            for (let i = 0; i < starRefs.current.length; i += 1) {
                const refGroup = starRefs.current[i];
                const starData = stars[i];
                if (!refGroup || !starData) continue;
                const deltaY = scrollY * (yBase + starData.depth * yDepthMult);
                const translateX = scrollY * (starData.depth - 0.5) * xBase; // drift left/right by depth

                const setTransform = (el, y) => {
                    if (!el) return;
                    // Snap to whole pixels to avoid subpixel blur/jitter
                    const tx = Math.round(translateX);
                    const ty = Math.round(-deltaY + y);
                    el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
                    el.style.willChange = 'transform';
                };

                // Primary at baseTopPx
                setTransform(refGroup.primary, starData.baseTopPx);
                // Clone below one viewport height
                setTransform(refGroup.cloneBelow, starData.baseTopPx + viewportHeightRef.current);
                // Clone above one viewport height
                setTransform(refGroup.cloneAbove, starData.baseTopPx - viewportHeightRef.current);
            }
        };

        let animationFrameId = null;
        const loop = () => {
            const current = getScrollY();
            if (current !== latestScrollYRef.current) {
                latestScrollYRef.current = current;
                applyParallax(current);
            }
            animationFrameId = window.requestAnimationFrame(loop);
        };

        // Kick off
        latestScrollYRef.current = getScrollY();
        applyParallax(latestScrollYRef.current);
        animationFrameId = window.requestAnimationFrame(loop);

        const handleResize = () => {
            viewportHeightRef.current = window.innerHeight || viewportHeightRef.current;
            applyParallax(latestScrollYRef.current);
        };
        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            if (animationFrameId != null) {
                window.cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [stars]);

    return (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 starfield-container">
            {stars.map((star) => (
                <React.Fragment key={star.id}>
                    {/* Primary */}
                    <div
                        className="absolute"
                        style={{ left: `${star.leftPercent}%`, top: `${star.baseTopPx}px` }}
                        ref={(el) => {
                            if (!starRefs.current[star.id]) starRefs.current[star.id] = {};
                            starRefs.current[star.id].primary = el;
                        }}
                    >
                        <div
                            className={`rounded-full star star-twinkle-real`}
                            style={{
                                width: `${star.size}px`,
                                height: `${star.size}px`,
                                '--opacity-min': star.opacityMin,
                                '--opacity-max': star.opacityMax,
                                '--twinkle-duration': `${star.animationDuration}s`,
                                '--twinkle-delay': `${star.animationDelay}s`,
                                background: star.useSolid
                                    ? `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${star.opacityMid})`
                                    : `radial-gradient(circle, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.95) 0%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.65) 45%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.0) 70%)`,
                                boxShadow: star.useSolid
                                    ? 'none'
                                    : `0 0 ${star.glowPx}px rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.5)`
                            }}
                        />
                    </div>

                    {/* Clone below */}
                    <div
                        className="absolute"
                        style={{ left: `${star.leftPercent}%`, top: `${star.baseTopPx}px` }}
                        ref={(el) => {
                            if (!starRefs.current[star.id]) starRefs.current[star.id] = {};
                            starRefs.current[star.id].cloneBelow = el;
                        }}
                    >
                        <div
                            className={`rounded-full star star-twinkle-real`}
                            style={{
                                width: `${star.size}px`,
                                height: `${star.size}px`,
                                '--opacity-min': star.opacityMin,
                                '--opacity-max': star.opacityMax,
                                '--twinkle-duration': `${star.animationDuration}s`,
                                '--twinkle-delay': `${star.animationDelay}s`,
                                background: star.useSolid
                                    ? `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${star.opacityMid})`
                                    : `radial-gradient(circle, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.95) 0%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.65) 45%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.0) 70%)`,
                                boxShadow: star.useSolid
                                    ? 'none'
                                    : `0 0 ${star.glowPx}px rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.5)`
                            }}
                        />
                    </div>

                    {/* Clone above */}
                    <div
                        className="absolute"
                        style={{ left: `${star.leftPercent}%`, top: `${star.baseTopPx}px` }}
                        ref={(el) => {
                            if (!starRefs.current[star.id]) starRefs.current[star.id] = {};
                            starRefs.current[star.id].cloneAbove = el;
                        }}
                    >
                        <div
                            className={`rounded-full star star-twinkle-real`}
                            style={{
                                width: `${star.size}px`,
                                height: `${star.size}px`,
                                '--opacity-min': star.opacityMin,
                                '--opacity-max': star.opacityMax,
                                '--twinkle-duration': `${star.animationDuration}s`,
                                '--twinkle-delay': `${star.animationDelay}s`,
                                background: star.useSolid
                                    ? `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${star.opacityMid})`
                                    : `radial-gradient(circle, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.95) 0%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.65) 45%, rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.0) 70%)`,
                                boxShadow: star.useSolid
                                    ? 'none'
                                    : `0 0 ${star.glowPx}px rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.5)`
                            }}
                        />
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

export default StarField; 